import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

import Fetcher from '@/api/Fetcher';
import { useOrganizationList } from '@/api/organization/organizationAPI';
import Error404 from '@/components/errors/Error404';
import PageError from '@/components/errors/PageError';
import { GlobalLoader } from '@/components/GlobalLoader';
import { useLogOut } from '@/hooks/useLogOut';
import useSessionExchange from '@/hooks/useSessionExchange';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router/routes';

const AppInstanceRedirect = () => {
  const { instance } = useParams();
  const location = useLocation();
  const user = useMe();
  const { exchangeSession } = useSessionExchange();
  const { logOut } = useLogOut();
  const stytch = useStytchB2BClient();
  const [isAllowed, setIsAllowed] = useState(true);
  const [instanceAllowed, setInstanceAllowed] = useState<boolean>(false);
  const [resolutionError, setResolutionError] = useState(false);
  const hasAttemptedExchange = useRef(false);

  const needsOrgResolution =
    instance && user?.currentOrg.homeInstance.replace(/^inst:/, '') !== instance;

  const { organizations, error: orgListError } = useOrganizationList({ searchQuery: '' });

  useEffect(() => {
    if (!needsOrgResolution || !organizations || hasAttemptedExchange.current) {
      return;
    }

    const org = organizations.find((o) => o.slug.replace(/^inst~/, '') === instance);

    const attemptToAuthOrg = async () => {
      hasAttemptedExchange.current = true;

      if (org) {
        // User is a member of this organization
        if (!org.isAuthenticated) {
          // Member needs to complete primary authentication (SSO, OAuth, etc.)
          // Redirect to tenant sign-in page - even superusers must authenticate to their own orgs
          window.location.href = ROUTES.signInInstanceBySlug(instance);
          return;
        }

        // The user belongs to the instance in the url, but the token is for a different instance.
        // Let's exchange the session automatically and keep the app blocked until it completes.
        try {
          await exchangeSession(
            {
              id: org.organizationId,
              name: org.name,
              instanceId: org.id,
            },
            {
              rememberLastAccess: true,
              clearCacheAndReload: false,
            },
          );
        } catch (e) {
          console.error(e);
          console.error('Unable to exchange session to the correct instance');
          setResolutionError(true);
        }
      } else {
        // User is NOT a member of this organization
        if (user?.isC99Superuser && instance) {
          // Superusers can access any org they don't belong to (backend authorizes superusers)
          setInstanceAllowed(true);
        } else {
          // Non-members who aren't superusers are not allowed
          setIsAllowed(false);
        }
      }
    };

    attemptToAuthOrg();
  }, [needsOrgResolution, organizations, instance, user, exchangeSession]);

  if (!user?.currentOrg.homeInstance) {
    return (
      <PageError
        message={<Trans>Unable to load the organization instance</Trans>}
        detail={
          <Trans>
            Please refresh the page. If this error continues to happen please contact support.
          </Trans>
        }
      />
    );
  }

  const tokenInstanceSlug = user?.currentOrg.homeInstance.replace(/^inst:/, '');

  if (!instance) {
    // The instance was not provided in the url. Put whatever instance is in the token on the url.
    const { pathname, search, hash } = location;

    if (
      pathname === '/' ||
      pathname === '/app' ||
      pathname === '/app/inst' ||
      pathname === '/app/inst/'
    ) {
      return <Navigate to={`/app/inst/${tokenInstanceSlug}`} replace />;
    }

    // If the path starts with /app, and not /app/inst/:instance, then redirect to
    // /app/inst/:instance and keep the rest of the path.
    if (pathname.startsWith('/app') && !pathname.startsWith('/app/inst')) {
      const subPath = pathname.replace('/app', '');
      return <Navigate to={`/app/inst/${tokenInstanceSlug}${subPath}${search}${hash}`} replace />;
    }
  }

  if (tokenInstanceSlug === instance || instanceAllowed) {
    Fetcher.setUserData(user, stytch, logOut);
    return <Outlet />;
  }

  if (needsOrgResolution) {
    if (orgListError || resolutionError) {
      return (
        <PageError
          message={<Trans>Unable to load the instance</Trans>}
          detail={
            <Trans>
              Please refresh the page. If this error continues to happen please contact support.
            </Trans>
          }
        />
      );
    }

    if (isAllowed) {
      return <GlobalLoader />;
    }
  }

  if (!isAllowed) {
    return (
      <Error404
        title={<Trans>Unable to load the instance</Trans>}
        subtitle={
          <Trans>
            This can happen if the instance doesn't exist or you don't have access to it.
            <p>
              If you don't have access to an instance, please contact your administrator for an
              invite. Otherwise, check that you typed the address correctly, go back to your
              previous page or return to the home page.
            </p>
          </Trans>
        }
      />
    );
  }

  return <Outlet />;
};

export default AppInstanceRedirect;
